import { useEffect, useMemo, useState } from 'react'
import type { TFunction } from 'i18next'
import { getColumnDefinitions, getColumnVisibilityKey } from './case-infrastructure-tab.helpers'

export interface ColumnVisibilityState {
  columnDefinitions: ReturnType<typeof getColumnDefinitions>
  visibleColumns: Set<string>
  showColumnSettings: boolean
  setShowColumnSettings: React.Dispatch<React.SetStateAction<boolean>>
  toggleColumn: (columnId: string) => void
  resetColumns: () => void
}

export function useCaseInfrastructureColumns(args: {
  caseId: string
  t: TFunction<'cases'>
}): ColumnVisibilityState {
  const { caseId, t } = args
  const columnDefinitions = useMemo(() => getColumnDefinitions(t), [t])
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(getColumnVisibilityKey(caseId))
    if (stored) {
      try {
        return new Set(JSON.parse(stored) as string[])
      } catch {
        // fall through to defaults
      }
    }
    return new Set(columnDefinitions.filter((col) => col.defaultVisible).map((col) => col.id))
  })
  const [showColumnSettings, setShowColumnSettings] = useState(false)

  useEffect(() => {
    localStorage.setItem(getColumnVisibilityKey(caseId), JSON.stringify([...visibleColumns]))
  }, [visibleColumns, caseId])

  const toggleColumn = (columnId: string): void => {
    setVisibleColumns((prev) => {
      const next = new Set(prev)
      if (next.has(columnId)) {
        if (columnId !== 'hostname' || next.size > 1) {
          next.delete(columnId)
        }
      } else {
        next.add(columnId)
      }
      return next
    })
  }

  const resetColumns = (): void => {
    setVisibleColumns(
      new Set(columnDefinitions.filter((col) => col.defaultVisible).map((col) => col.id))
    )
  }

  return {
    columnDefinitions,
    visibleColumns,
    showColumnSettings,
    setShowColumnSettings,
    toggleColumn,
    resetColumns,
  }
}
